import { AnyAction } from "@reduxjs/toolkit";
import { map, mergeMap } from "rxjs/operators";

import { Handler } from "@tsp/api";
import { parseServerError } from "@tsp/api/utils/parseServerError";
import { Epic, combineEpics, ofAction } from "@tsp/utils/epics";
import { catchAsyncError } from "@tsp/utils/epics/catchAsyncError";

import { showNotification } from "@tsp-core/notifier/slice";

import { loadSupplierActualWorkTypesAction } from "../actions";
import { LoadSupplierActualWorkTypesRequestParams, LoadSupplierActualWorkTypesResponseData } from "../api";
import {
  translateLoadSupplierActualWorkTypesActionParamsToLoadSupplierActualWorkTypesRequestParams,
  translateLoadSupplierActualWorkTypesResponseDataToLoadSupplierActualWorkTypesActionResult,
} from "../mappers";

export type SupplierWorkTypesEpicDependencies = {
  api: {
    loadSupplierActualWorkTypes: Handler<
      LoadSupplierActualWorkTypesRequestParams,
      LoadSupplierActualWorkTypesResponseData
    >;
  };
};

const onLoadSupplierActualWorkTypesStartedEpic: Epic<null, SupplierWorkTypesEpicDependencies> = (actions$, _, deps) => {
  return actions$.pipe(
    ofAction(loadSupplierActualWorkTypesAction.started),
    mergeMap((action) => {
      const request = translateLoadSupplierActualWorkTypesActionParamsToLoadSupplierActualWorkTypesRequestParams(
        action.payload,
      );

      return deps.api.loadSupplierActualWorkTypes(request).pipe(
        map((response) => {
          return loadSupplierActualWorkTypesAction.done({
            params: action.payload,
            result: translateLoadSupplierActualWorkTypesResponseDataToLoadSupplierActualWorkTypesActionResult(response),
          });
        }),
        catchAsyncError((error) => {
          return loadSupplierActualWorkTypesAction.failed({
            params: action.payload,
            error: parseServerError(error),
          });
        }),
      );
    }),
  );
};

const onFailureActionEpic: Epic<null, SupplierWorkTypesEpicDependencies> = (actions$) => {
  return actions$.pipe(
    ofAction(loadSupplierActualWorkTypesAction.failed),
    mergeMap((action) => {
      if (typeof action.payload.error === "string") {
        return [
          showNotification({
            type: "error",
            message: action.payload.error,
          }) as unknown as AnyAction,
        ];
      }

      return [];
    }),
  );
};

export const supplierWorkTypesEpic = combineEpics(onLoadSupplierActualWorkTypesStartedEpic, onFailureActionEpic);
